import { useEffect } from "react";

import { useForm } from "react-hook-form";

import { TextInput } from "../shared/Inputs/TextInput";
import Modal, { ModalProps } from "../../layouts/Modal";
import { IAccount } from "../../models/account/IAccount";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import useAccountAddresses from "../../data/useAccountAddresses";
import useAccountMutations from "../../data/useAccountMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useAccountAddressMutations from "../../data/useAccountAddressMutations";
import {
  AccountAddressCreateEditFormFields,
  IAccountAddress,
} from "./../../models/account/IAccountAddress";
import PickAddress from "../shared/Addresses/PickAddress";

interface AccountAddressCreateEditModalProps
  extends Omit<ModalProps, "children" | "width"> {
  account: IAccount;
  address?: IAccountAddress;
  onSaved?: (address?: IAccountAddress) => void;
}

export function AccountAddressCreateEditModal({
  open,
  setOpen,
  account,
  address,
  onSaved,
}: AccountAddressCreateEditModalProps) {
  const { register, setValue, watch, handleSubmit, reset } =
    useForm<AccountAddressCreateEditFormFields>();
  const { update: updateAccount } = useAccountMutations(account.id, "AccountAddressCreateEditModal");
  const { create: createAccountAddress } = useAccountAddresses(account.id, "AccountAddressCreateEditModal");
  const { update: updateAccountAddress } = useAccountAddressMutations(
    account.id,
    address?.id,
    "AccountAddressCreateEditModal"
  );
  const { saveHandlers } = useDefaultCRUDHandlers("Address");
  const isBillingAddress = !!address && address.id === account.billingAddressId;
  const isDeliveryAddress = !!address && address.id === account.shippingAddressId;

  const updateDefaultAddress = (addressIds: {
    billingAddressId?: string;
    shippingAddressId?: string;
  }) => {
    updateAccount.mutate({ ...account, ...addressIds }, saveHandlers);
  };

  const onSubmit = (data: AccountAddressCreateEditFormFields) => {
    if (address) {
      updateAccountAddress.mutate(data, {
        ...saveHandlers,
        onSuccess: (res: any) => {
          saveHandlers.onSuccess();
          onSaved?.(res?.data);
          setOpen(false);
        },
      });
    } else {
      createAccountAddress.mutate(data, {
        ...saveHandlers,
        onSuccess: (res: any) => {
          saveHandlers.onSuccess();
          onSaved?.(res?.data);
          setOpen(false);
        },
      });
    }
  };

  useEffect(() => {
    if (address) {
      reset(address);
    } else {
      reset({});
    }
  }, [address, open, reset]);

  return (
    <Modal width="max-w-2xl" open={open} setOpen={setOpen}>
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-xl font-semibold">{`${address ? "Edit" : "Add"} Address`}</h2>
        {address && (
          <div className="flex items-center gap-2">
            {isBillingAddress && (
              <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700">
                Billing
              </span>
            )}
            {isDeliveryAddress && (
              <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700">
                Delivery
              </span>
            )}
          </div>
        )}
      </div>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="grid grid-cols-1 sm:grid-cols-2 gap-4"
      >
        <TextInput label="Name" register={register} registerName="name" cols="col-span-2" rounded />

        <PickAddress register={register} setValue={setValue} watch={watch} classes="col-span-2" rounded />
        {/* <TextInput label="Line 1" register={register} registerName="line1" />
        <TextInput label="Line 2" register={register} registerName="line2" />
        <TextInput label="Line 3" register={register} registerName="line3" />
        <TextInput label="Line 4" register={register} registerName="line4" />
        <TextInput
          label="Locality"
          register={register}
          registerName="locality"
        />
        <TextInput label="Town" register={register} registerName="town" />
        <TextInput label="County" register={register} registerName="county" />
        <TextInput
          label="Postcode"
          register={register}
          registerName="postcode"
        /> */}
        {address && (
          <div className="sm:col-span-2 mt-4 sm:mt-0 flex flex-col items-stretch sm:items-end space-y-2">
            {!isBillingAddress && (
              <ButtonInput
                label="Set as Billing Address"
                isSubmit={false}
                classes=""
                onClick={() =>
                  updateDefaultAddress({ billingAddressId: address.id })
                }
              />
            )}
            {!isDeliveryAddress && (
              <ButtonInput
                label="Set as Delivery Address"
                isSubmit={false}
                classes=""
                onClick={() =>
                  updateDefaultAddress({
                    shippingAddressId: address.id,
                  })
                }
              />
            )}
          </div>
        )}
        <ButtonInput
          label="Save"
          isSubmit={true}
          classes="mt-6"
          onClick={undefined}
        />
      </form>
    </Modal>
  );
}
